<template>
  <div class="shortcut-tile-card flex flex-col justify-between" @click.stop="onClickCard">
    <!-- Top accent bar -->
    <div class="shortcut-accent-bar" :style="{ background: hex_color }"></div>

    <div class="p-4 flex flex-col justify-between flex-1">
      <!-- Main Content Header with Month Selector on Top -->
      <div class="flex items-center justify-between">
        <div class="flex items-center min-w-0 pr-2">
          <!-- Icon Container -->
          <div
            class="shortcut-icon-wrapper flex items-center justify-center rounded-xl mr-3 flex-shrink-0"
            :style="{ background: rgb_color, color: hex_color, border: `1px solid ${rgb_color_border}` }"
          >
            <feather-icon
              :icon="icon || 'BoxIcon'"
              svgClasses="h-5 w-5"
              :style="{ color: hex_color }"
            />
          </div>

          <!-- Text Info -->
          <div class="min-w-0">
            <h4 class="shortcut-title font-bold text-base mb-0.5 leading-tight truncate" :title="title">
              {{ title }}
            </h4>
            <p class="shortcut-desc text-xs truncate">
              Monthly sales & delivery performance
            </p>
          </div>
        </div>

        <!-- Month Selector at the Top Right -->
        <div class="month-selector-top-wrapper relative flex-shrink-0 ml-1" @click.stop>
          <button
            type="button"
            class="month-selector-top-btn flex items-center"
            :class="{ 'is-active': show_month_dropdown }"
            @click.stop="toggleMonthDropdown"
            title="Select Month"
          >
            <feather-icon icon="CalendarIcon" svgClasses="h-3 w-3 mr-1 text-primary" />
            <span class="month-selected-text">{{ selectedMonthName }}</span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-3 w-3 ml-1 chevron-arrow" />
          </button>

          <!-- Month Dropdown Popover -->
          <transition name="dropdown-fade">
            <div
              v-if="show_month_dropdown"
              class="shortcut-month-dropdown"
              @click.stop
            >
              <div class="month-dropdown-header flex items-center justify-between">
                <span class="month-header-title">Select Month</span>
                <span class="month-header-year">{{ currentYear }}</span>
              </div>
              <div class="month-grid">
                <button
                  v-for="(m, index) in monthsList"
                  :key="index"
                  type="button"
                  class="month-grid-btn"
                  :class="{ 'is-selected': selectedMonthIndex === index }"
                  :style="selectedMonthIndex === index ? { backgroundColor: hex_color, color: '#ffffff' } : {}"
                  @click.stop="onSelectMonth(index)"
                >
                  {{ m.short }}
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- 3 Metrics Section: Shipped, Expected to Ship, Total Sales -->
      <div class="shortcut-stats-row" :class="{ 'is-loading-stats': is_loading }">
        <!-- 1. Shipped -->
        <div class="shortcut-stat-pill stat-shipped" @click.stop="onClickTile($event,'shipped')">
          <span class="stat-pill-label" title="Shipped">Shipped</span>
            <span class="stat-pill-value stat-val-shipped">
              <span v-if="is_loading" class="loading-placeholder">...</span>
              <span v-else>{{ formatStatDisplay(sales_data.shipped) }}</span>
            </span>
        </div>

        <!-- 2. Expected to Ship -->
        <div class="shortcut-stat-pill stat-expected" @click.stop="onClickTile($event,'expected')">
          <span class="stat-pill-label" title="Expected to Ship">Expected to Ship</span>
            <span class="stat-pill-value stat-val-expected">
              <span v-if="is_loading" class="loading-placeholder">...</span>
              <span v-else>{{ formatStatDisplay(sales_data.expected_to_ship) }}</span>
            </span>
        </div>

        <!-- 3. Total Sales -->
        <div class="shortcut-stat-pill stat-target">
          <span class="stat-pill-label" title="Target">Target</span>
            <span class="stat-pill-value stat-val-target" :style="{ color: hex_color }">
              <span v-if="is_loading" class="loading-placeholder">...</span>
              <span v-else>{{ formatStatDisplay(sales_data.total_target) }}</span>
            </span>
        </div>
        <div class="shortcut-stat-pill stat-target">
          <span class="stat-pill-label" title="Total Expected in period">Total Expected in period</span>
            <span class="stat-pill-value stat-val-target" :style="{ color: hex_color }">
              <span v-if="is_loading" class="loading-placeholder">...</span>
              <span v-else>{{ formatStatDisplay(sales_data.period) }}</span>
            </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import _color from "@/assets/utils/color.js";
import { RepositoryFactory } from "@/repository/RepositoryFactory";
const ShortcutsRepository = RepositoryFactory.get("shortcuts");

export default {
  name: "SalesOrdersShortcutCard",
  props: {
    title: {
      type: String,
      default: "Sales Orders",
    },
    icon: {
      type: String,
      default: "BoxIcon",
    },
    color: {
      type: String,
      default: "#7367f0",
    },
    url: {
      type: String,
      default: "/pages/product-orders",
    },
    currency: {
      type: String,
      default: "GBP",
    },
    is_edit_mode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const now = new Date();
    return {
      is_loading: false,
      show_month_dropdown: false,
      selectedMonthIndex: now.getMonth(),
      currentYear: now.getFullYear(),
      currency_code: "GBP",
      monthsList: [
        { short: "Jan", full: "January" },
        { short: "Feb", full: "February" },
        { short: "Mar", full: "March" },
        { short: "Apr", full: "April" },
        { short: "May", full: "May" },
        { short: "Jun", full: "June" },
        { short: "Jul", full: "July" },
        { short: "Aug", full: "August" },
        { short: "Sep", full: "September" },
        { short: "Oct", full: "October" },
        { short: "Nov", full: "November" },
        { short: "Dec", full: "December" },
      ],
      sales_data: {
        shipped: null,
        expected_to_ship: null,
        total_target: null,
        period: null,
      },
    };
  },
  mounted() {
    this.fetchSalesOrderData(this.selectedMonthIndex);
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  computed: {
    selectedMonthName() {
      return this.monthsList[this.selectedMonthIndex] ? this.monthsList[this.selectedMonthIndex].short : "";
    },
    selectedMonthFullName() {
      return this.monthsList[this.selectedMonthIndex] ? this.monthsList[this.selectedMonthIndex].full : "";
    },
    currency_symbol() {
      const c = String(this.currency_code || this.currency || "GBP").toUpperCase().trim();
      return this.__currencySymbol(c) || this.currency_code || "£";
    },
    rgb_color() {
      if (_color.isHexColor(this.color)) {
        return _color.getColor(this.color, 0.12);
      }
      return _color.getColor("#7367f0", 0.12);
    },
    rgb_color_border() {
      if (_color.isHexColor(this.color)) {
        return _color.getColor(this.color, 0.28);
      }
      return _color.getColor("#7367f0", 0.28);
    },
    hex_color() {
      if (_color.isHexColor(this.color)) {
        return this.color;
      }
      return "#7367f0";
    },
  },
  methods: {
    onClickCard() {
      if (this.is_edit_mode) return;
      if (this.url) {
        this.$router.push(this.url).catch(() => {});
      }
    },
    onClickTile(e, type) {
      if (e) e.stopPropagation();
      if (this.is_edit_mode) return;

      const fromDate = moment().year(this.currentYear).month(this.selectedMonthIndex).startOf("month").format("DD/MM/YYYY");
      const toDate = moment().year(this.currentYear).month(this.selectedMonthIndex).endOf("month").format("DD/MM/YYYY");
      this.$router
        .push({
          path: this.url || "/pages/product-orders",
          query: {
            "type": type,
            "shipment-date-from": fromDate,
            "shipment-date-to": toDate,
          },
        })
        .catch(() => {});
    },
    toggleMonthDropdown() {
      this.show_month_dropdown = !this.show_month_dropdown;
    },
    handleOutsideClick() {
      this.show_month_dropdown = false;
    },
    onSelectMonth(index) {
      this.selectedMonthIndex = index;
      this.show_month_dropdown = false;
      this.fetchSalesOrderData(index);
    },
    async fetchSalesOrderData(monthIndex) {
      const monthNumber = monthIndex + 1;
      this.is_loading = true;
      try {
        const url = `/admin/report/sales-order-total?interval=monthly&month=${monthNumber}`;
        const response = await ShortcutsRepository.getShortCutData(url);
        if (response && response.data && response.data.data) {
          const d = response.data.data;
          this.currency_code = d.currency || this.currency || "GBP";
          this.sales_data = {
            shipped: d.shipped && d.shipped.value !== undefined ? d.shipped.value : d.shipped,
            expected_to_ship: d.expected && d.expected.value !== undefined ? d.expected.value : d.expected,
            total_target: d.target && d.target.value !== undefined ? d.target.value : d.target,
            period: d.period && d.period.value !== undefined ? d.period.value : d.period,
          };
        }
      } catch (error) {
        console.error("Error fetching sales order monthly data:", error);
      } finally {
        this.is_loading = false;
      }
    },
    formatStatDisplay(rawVal) {
      if (rawVal === undefined || rawVal === null || rawVal === "" || rawVal === "-") {
        return "-";
      }

      const cleanStr = String(rawVal).replace(/,/g, "").trim();
      const numVal = typeof rawVal === "number" ? rawVal : parseFloat(cleanStr);

      if (isNaN(numVal)) {
        return `${this.currency_symbol} ${rawVal}`;
      }

      const prefix = this.currency_symbol ? `${this.currency_symbol} ` : "";
      const abs = Math.abs(numVal);

      if (abs >= 1000000000) {
        return `${prefix}${(numVal / 1000000000).toFixed(2).replace(/\.?0+$/, "")}B`;
      }
      if (abs >= 1000000) {
        return `${prefix}${(numVal / 1000000).toFixed(2).replace(/\.?0+$/, "")}M`;
      }
      if (abs >= 10000) {
        return `${prefix}${(numVal / 1000).toFixed(1).replace(/\.?0+$/, "")}k`;
      }

      if (Number.isInteger(numVal)) {
        return `${prefix}${numVal.toLocaleString()}`;
      }

      return `${prefix}${numVal.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}`;
    },
    formatFullCurrency(rawVal) {
      if (rawVal === undefined || rawVal === null || rawVal === "" || rawVal === "-") {
        return "-";
      }

      const cleanStr = String(rawVal).replace(/,/g, "").trim();
      const numVal = typeof rawVal === "number" ? rawVal : parseFloat(cleanStr);

      if (isNaN(numVal)) {
        return `${this.currency_symbol} ${rawVal}`;
      }

      const prefix = this.currency_symbol ? `${this.currency_symbol} ` : "";
      return `${prefix}${numVal.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.shortcut-tile-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.04);
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  height: 100%;
  min-height: 84px;
  overflow: visible;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    border-color: rgba(var(--vs-primary), 0.35);

    .shortcut-icon-wrapper {
      transform: scale(1.06);
    }
  }

  .shortcut-accent-bar {
    height: 3px;
    width: 100%;
    opacity: 0.9;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  .shortcut-icon-wrapper {
    width: 40px;
    height: 40px;
    transition: transform 0.2s ease;
  }

  .shortcut-title {
    color: #2c2c2c;
  }

  .shortcut-desc {
    color: #727b87;
    line-height: 1.3;
  }

  /* Top Month Selector */
  .month-selector-top-wrapper {
    position: relative;
  }

  .month-selector-top-btn {
    display: inline-flex;
    align-items: center;
    padding: 3px 7px;
    font-size: 11px;
    font-weight: 600;
    color: #5e5873;
    border-radius: 7px;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
    cursor: pointer;
    line-height: 1.2;
    transition: all 0.15s ease;
    user-select: none;
    outline: none;

    &:hover,
    &.is-active {
      background: rgba(var(--vs-primary), 0.12);
      border-color: rgba(var(--vs-primary), 0.35);
      color: rgba(var(--vs-primary), 1);

      .chevron-arrow {
        transform: rotate(180deg);
      }
    }

    .chevron-arrow {
      transition: transform 0.2s ease;
    }
  }

  .shortcut-month-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 175px;
    background: #ffffff;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 6px 12px -4px rgba(0, 0, 0, 0.08);
    padding: 8px;
    z-index: 120;
    cursor: default;

    .month-dropdown-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      padding-bottom: 4px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);

      .month-header-title {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        color: #5e5873;
        letter-spacing: 0.3px;
      }

      .month-header-year {
        font-size: 10px;
        font-weight: 700;
        color: #5e5873;
      }

      .year-nav-btn {
        padding: 1px 3px;
        border-radius: 4px;
        border: none;
        background: rgba(0, 0, 0, 0.04);
        color: #727b87;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
          background: rgba(var(--vs-primary), 0.15);
          color: rgba(var(--vs-primary), 1);
        }
      }
    }

    .month-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 4px;

      .month-grid-btn {
        padding: 4px 2px;
        font-size: 10px;
        font-weight: 600;
        text-align: center;
        border-radius: 6px;
        border: none;
        background: rgba(0, 0, 0, 0.035);
        color: #5e5873;
        cursor: pointer;
        transition: all 0.15s ease;
        outline: none;

        &:hover {
          background: rgba(var(--vs-primary), 0.14);
          color: rgba(var(--vs-primary), 1);
        }

        &.is-selected {
          color: #ffffff !important;
          font-weight: 700;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }
      }
    }
  }

  /* 3 Stats Row */
  .shortcut-stats-row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    margin-left: -3px;
    margin-right: -3px;
    gap: 5px;
    transition: opacity 0.2s ease;

    &.is-loading-stats {
      opacity: 0.65;
    }
  }

  .shortcut-stat-pill {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1 1 0%;
    min-width: 0;
    margin: 0;
    padding: 5px 6px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.035);
    border: 1px solid rgba(0, 0, 0, 0.04);
    overflow: hidden;

    &.stat-shipped {
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(40, 199, 111, 0.12);
        border-color: rgba(40, 199, 111, 0.4);
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(40, 199, 111, 0.18);
      }
    }

    &.stat-expected {
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 159, 0, 0.12);
        border-color: rgba(255, 159, 0, 0.4);
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(255, 159, 0, 0.18);
      }
    }

    .stat-pill-label {
      color: #7d8793;
      font-size: 8.5px;
      font-weight: 600;
      letter-spacing: 0.2px;
      text-transform: uppercase;
      margin-bottom: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      width: 100%;
    }

    .stat-pill-tooltip {
      min-width: 0;
      display: block;
      overflow: hidden;
      max-width: 100%;
    }

    .stat-pill-value {
      font-size: 11.5px;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      max-width: 100%;
      line-height: 1.2;

      &.stat-val-shipped {
        color: #28c76f;
      }

      &.stat-val-expected {
        color: #ff9f43;
      }
    }

    .loading-placeholder {
      font-weight: 400;
      letter-spacing: 1px;
    }
  }
}

/* Dropdown Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: top right;
}
.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.95);
}

/* Dark Theme Support */
::v-deep .theme-dark,
.theme-dark {
  .shortcut-tile-card {
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
    background-color: #10163a;

    &:hover {
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
      border-color: rgba(115, 103, 240, 0.5);
    }

    .shortcut-title {
      color: #ebeefd;
    }

    .shortcut-desc {
      color: #9aa2ba;
    }

    .month-selector-top-btn {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.12);
      color: #c2c6dc;

      &:hover,
      &.is-active {
        background: rgba(115, 103, 240, 0.25);
        border-color: rgba(115, 103, 240, 0.5);
        color: #ebeefd;
      }
    }

    .shortcut-month-dropdown {
      background: #1e2746;
      border-color: rgba(255, 255, 255, 0.12);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.6);

      .month-dropdown-header {
        border-bottom-color: rgba(255, 255, 255, 0.08);

        .month-header-title {
          color: #c2c6dc;
        }

        .month-header-year {
          color: #c2c6dc;
        }

        .year-nav-btn {
          background: rgba(255, 255, 255, 0.08);
          color: #a3adc2;

          &:hover {
            background: rgba(115, 103, 240, 0.3);
            color: #ffffff;
          }
        }
      }

      .month-grid {
        .month-grid-btn {
          background: rgba(255, 255, 255, 0.06);
          color: #c2c6dc;

          &:hover {
            background: rgba(115, 103, 240, 0.3);
            color: #ffffff;
          }
        }
      }
    }

    .shortcut-stats-row {
      border-top-color: rgba(255, 255, 255, 0.07);
    }

    .shortcut-stat-pill {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.06);

      .stat-pill-label {
        color: #a3adc2;
      }
    }
  }
}
</style>
